import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { listTours, type TourListItem } from "@/lib/tours";
import TourCard from "@/pages/tours/components/TourCard";

export default function FeaturedTours() {
  const { t, i18n } = useTranslation("home");
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [tours, setTours] = useState<TourListItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    // Prefer the admin-curated featured tours; fall back to top-rated if none set.
    listTours({ featured: true, pageSize: 10 })
      .then(async (featured) => {
        const data = featured.length > 0 ? featured : await listTours({ sort: "rating", pageSize: 10 });
        if (!cancelled) setTours(data);
      })
      .catch(() => { if (!cancelled) setTours([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const checkScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 10);
  };

  const scroll = (dir: "left" | "right") => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dir === "left" ? -320 : 320, behavior: "smooth" });
    setTimeout(checkScroll, 350);
  };

  const priceLocale = i18n.language === "es" ? "es-MX" : "en-US";

  return (
    <section className="w-full px-4 md:px-8 lg:px-12 py-14 md:py-20">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-8 md:mb-10">
          <h2 className="font-display text-2xl md:text-3xl font-bold text-charcoal">
            {t("featured.title")}
          </h2>
          <div className="flex items-center gap-2">
            <button
              onClick={() => scroll("left")}
              disabled={!canScrollLeft}
              className="w-10 h-10 flex items-center justify-center rounded-full border border-gray-200 hover:border-ocean hover:text-ocean transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              aria-label={t("featured.scrollLeft")}
            >
              <i className="ri-arrow-left-line" />
            </button>
            <button
              onClick={() => scroll("right")}
              disabled={!canScrollRight}
              className="w-10 h-10 flex items-center justify-center rounded-full border border-gray-200 hover:border-ocean hover:text-ocean transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              aria-label={t("featured.scrollRight")}
            >
              <i className="ri-arrow-right-line" />
            </button>
          </div>
        </div>

        <div
          ref={scrollRef}
          onScroll={checkScroll}
          className="flex gap-4 md:gap-6 overflow-x-auto scrollbar-hide pb-4 -mx-4 px-4 md:-mx-0 md:px-0"
          style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
        >
          {loading
            ? Array.from({ length: 4 }).map((_, i) => (
                <div
                  key={i}
                  className="shrink-0 w-[280px] md:w-[320px] h-[360px] bg-gray-100 rounded-2xl animate-pulse"
                />
              ))
            : tours.map((tour) => (
                <div key={tour.id} className="shrink-0 w-[280px] md:w-[320px]">
                  <TourCard tour={tour} priceLocale={priceLocale} />
                </div>
              ))}
        </div>
      </div>
    </section>
  );
}